import { useCallback } from 'react';
import { getUserProfileCall } from '../../core/state/api';
import { FeatureFlag, TutorialKeys } from '../constants';
import { useFeatureFlags } from './useFeatureFlag';

/**
 * Custom hook to check if a specific tutorial has been seen by the user.
 * The check is based on a combination of the tutorial key, the user's email, and the active context, which are stored in localStorage.
 * The hook also respects a feature flag (TUTORIAL) to determine whether to show the tutorial or not. If the feature flag is disabled, it will always return true, indicating that the tutorial has been seen.
 * It's used email instead of userId to ensure the uniqueness of the key across different organizations, userId it's not unique across orgs but email is.
 * If hasAlreadySeenTutorial returns false, it means that the user has not seen the tutorial yet, and it will set the localStorage item to 'true' to mark it as seen for future checks.
 *
 * @example
 * const { hasAlreadySeenTutorial } = useTutorialCheck();
 * if (!hasAlreadySeenTutorial(TutorialKeys.ONBOARDING)) {
 *   openOnboardingTutorialDialog();
 * }
 *
 * @returns {Object} An object containing the `hasAlreadySeenTutorial` function.
 */
export const useTutorialCheck = () => {
  const { activeContext, email } = getUserProfileCall.hook();
  const { isFeatureEnabled, flagsAvailable } = useFeatureFlags();
  const isFFTutorial = isFeatureEnabled(FeatureFlag.TUTORIAL);

  const tutorialKeyGenerator = (baseKey: string, platformName: string, userId: string) =>
    `${baseKey}-${platformName}-${userId}`;

  /**
   * Checks if the user has already seen the specified tutorial.
   * If the tutorial feature flag is enabled and the user has not seen the tutorial, it marks it as seen in localStorage and returns false.
   * If the FF is disabled, or if the user profile or flags are not ready, it returns true to avoid showing the tutorial unnecessarily.
   *
   * @param {TutorialKeys} tutorialKey - The key for the tutorial to check.
   * @returns {boolean} - Returns `true` if the tutorial has been seen, or if the FF or user profile is not ready; otherwise, returns `false` and sets the localStorage item to 'true'.
   */
  const hasAlreadySeenTutorial = useCallback(
    (tutorialKey: TutorialKeys) => {
      // If FF or getUserProfileCall is not ready, return true to avoid showing the tutorial unnecessarily.
      if (!email || !activeContext || !flagsAvailable) {
        return true;
      }
      const completeTutorialKey = tutorialKeyGenerator(tutorialKey, activeContext, email);
      const hasSeenTutorialValue = localStorage.getItem(completeTutorialKey);
      if (isFFTutorial && hasSeenTutorialValue !== 'true') {
        localStorage.setItem(completeTutorialKey, 'true');
        return false;
      }
      return true;
    },
    [isFFTutorial, email, activeContext, flagsAvailable],
  );

  return {
    hasAlreadySeenTutorial,
  };
};
